import { EastRounded, FeedOutlined } from "@mui/icons-material";
import { Box, Button, LinearProgress } from "@mui/material";
import { grey } from "@mui/material/colors";
import { isEqual, isNil } from "lodash";
import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import FlxGrid from "../../../../../_src_shared/components/layout/FlxGrid";
import { formatDateTime } from "../../../../../_src_shared/utils/functions/formatDateTime";
import EditableDataRow from "../../../../../components/common/EditableDataRow";
import ProgressStepper from "../../../../../components/common/ProgressStepper";
import OverviewItem from "../../../../../components/data/OverviewItem";
import RffLoanDataGroup from "../../../../../components/finalForm/shared/RffLoanDataGroup";
import SelectInput from "../../../../../components/inputs/SelectInput";
import Flx from "../../../../../components/layout/Flx";
import ScreenContent from "../../../../../components/layout/ScreenContent";
import LoadingFallback from "../../../../../components/LoadingFallback";
import Txt from "../../../../../components/typography/Txt";
import TitledCard from "../../../../../components/ui/TitledCard";
import TitledGroup from "../../../../../components/ui/TitledGroup";
import { selectOptionsInterestCalcType } from "../../../../../constants/selectOptions/selectOptionsInterestCalcType";
import { selectOptionsLoanProductType } from "../../../../../constants/selectOptions/selectOptionsLoanProductType";
import { selectOptionsLoanPurpose } from "../../../../../constants/selectOptions/selectOptionsLoanPurpose";
import { selectOptionsLoanStatus } from "../../../../../constants/selectOptions/selectOptionsLoanStatus";
import { selectOptionsPipelineType } from "../../../../../constants/selectOptions/selectOptionsPipelineType";
import { useUnderwritingHook } from "../../../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../../../redux/actions/loanDrilldownActions";
import {
  loanPipelineClear,
  loanPipelineSet,
} from "../../../../../redux/actions/loanPipelineActions";
import { getUserFullNameFromEmail } from "../../../../../utils/functions/getUserFullNameFromEmail";

const SummaryTabSummaryCard = ({ baseUrl }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const users = useSelector((state) => state.users);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { updateLoanData } = useUnderwritingHook();

  // This function is called when user clicks "Save" in the row
  const handleUpdateField = useCallback(
    ({ field, newValue, stopLoadingFn }) => {
      // Skip update if values are the same
      const oldValueDerived = loanDrilldown[field];
      if (isEqual(newValue, oldValueDerived)) return;

      // Example: call your API to update data
      updateLoanData({
        loanId: loanDrilldown?._id,
        data: { [field]: newValue },
        onSuccessFn: (res) => {
          dispatch(loanDrilldownSet(res));
          dispatch(loanPipelineClear());
          // let the "loading" spinner turn off
          if (stopLoadingFn) stopLoadingFn();
        },
        onFailFn: () => {
          dispatch(loanDrilldownSet(loanDrilldown));
        },
      });
    },
    [loanDrilldown, updateLoanData, dispatch]
  );

  return (
    <>
      <Flx fw jb ac wrap gap={8}>
        <LoanProgressStepper />
        <LoanStatusField />
      </Flx>

      <TitledCard
        title={"Summary"}
        icon={<FeedOutlined />}
        variant="h3"
        headerEndContent={
          <Flx
            gap={4}
            sx={{
              position: "absolute",
              top: 24,
              right: 24,
            }}
          >
            <OverviewItem
              label={"Created"}
              value={loanDrilldown?.createDateTime}
              type={"date"}
              stacked={false}
            />
            <OverviewItem
              label={"Expected Closing Date"}
              value={loanDrilldown?.expectedClosingDate}
              type={"date"}
              stacked={false}
            />
          </Flx>
        }
      >
        <Suspense fallback={<LoadingFallback />}>
          <ScreenContent suppressPadding>
            <Flx column gap={5.5}>
              <Flx
                gap={1}
                fw
                sx={{
                  justifyContent: "space-between",
                  // mt: 3,
                  background: grey[100],
                  border: "1px solid #e2e8f0",
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                }}
              >
                <OverviewItem
                  // label="Base LTAIV"
                  label="Base Loan Amount"
                  // labelTooltip="Base Loan To As Is Value"
                  value={loanDrilldown?.baseLoanAmount}
                  type={"dollar"}
                />
                <OverviewItem
                  // label="Base LTAIV"
                  label="Base Loan To As Is Value"
                  // labelTooltip="Base Loan To As Is Value"
                  value={loanDrilldown?.baseLoanToAsIsValue}
                  type={"percent"}
                />

                <OverviewItem
                  // label="Base LTC"
                  label="Base Loan To Cost"
                  // labelTooltip="Base Loan To Cost"
                  value={loanDrilldown?.baseLoanToCost}
                  type={"percent"}
                />

                <OverviewItem
                  // label="Base LTPP"
                  label="Base Loan To Purchase Price"
                  // labelTooltip="Base Loan To Purchase Price"
                  value={loanDrilldown?.baseLoanToPurchasePrice}
                  type={"percent"}
                />
                <OverviewItem
                  label="Discount Points"
                  value={loanDrilldown?.discountPoints}
                  // type={"percent"}
                />
                <OverviewItem
                  label="Origination Points"
                  value={loanDrilldown?.originationPoints}
                  // type={"percent"}
                />
              </Flx>

              <FlxGrid width="340px" gap={8} sx={{ px: 1 }}>
                <TitledGroup
                  title={"Overview"}
                  variant="h5"
                  headerSx={{ p: 0 }}
                  bodySx={{ pl: 0, pr: 0, pb: 0, pt: 1 }}
                  fontWeight={600}
                  suppressPadding
                >
                  <RffLoanDataGroup column initialValues={loanDrilldown}>
                    <EditableDataRow
                      cellValueSize={5}
                      name="pipeline"
                      label="Pipeline"
                      type="select"
                      options={selectOptionsPipelineType}
                      onUpdateFn={handleUpdateField}
                      data={loanDrilldown}
                    />
                    <EditableDataRow
                      cellValueSize={5}
                      name="loanProductType"
                      label="Loan Product Type"
                      type="select"
                      options={selectOptionsLoanProductType}
                      onUpdateFn={handleUpdateField}
                      data={loanDrilldown}
                    />
                    <EditableDataRow
                      cellValueSize={5}
                      name="loanPurpose"
                      label="Loan Purpose"
                      type="select"
                      options={selectOptionsLoanPurpose}
                      onUpdateFn={handleUpdateField}
                      data={loanDrilldown}
                    />
                    <EditableDataRow
                      cellValueSize={5}
                      name="expectedClosingDate"
                      label="Expected Closing Date"
                      type="date"
                      onUpdateFn={handleUpdateField}
                      data={loanDrilldown}
                    />
                  </RffLoanDataGroup>
                </TitledGroup>

                <TitledGroup
                  title={"Terms"}
                  variant="h5"
                  fontWeight={600}
                  headerSx={{ p: 0 }}
                  bodySx={{ pl: 0, pr: 0, pb: 0, pt: 1 }}
                  suppressPadding
                >
                  <RffLoanDataGroup column>
                    <EditableDataRow
                      cellValueSize={4}
                      name="baseLoanAmount"
                      label="Base Loan Amount"
                      type="dollar"
                      onUpdateFn={handleUpdateField}
                      data={loanDrilldown}
                    />

                    <TermsRenderer
                      cellValueSize={4}
                      // cellValueSize={cellValueSize}
                      onUpdateFn={handleUpdateField}
                    />
                  </RffLoanDataGroup>
                </TitledGroup>

                <TitledGroup
                  title={"Assignee's"}
                  variant="h5"
                  fontWeight={600}
                  headerSx={{ p: 0 }}
                  bodySx={{ pl: 0, pr: 0, pb: 0, pt: 1 }}
                  suppressPadding
                >
                  <RffLoanDataGroup column initialValues={loanDrilldown}>
                    <EditableDataRow
                      cellValueSize={5}
                      placeholder="—"
                      name="salesperson"
                      label="Salesperson"
                      type="selectUserEmail"
                      onUpdateFn={handleUpdateField}
                      data={loanDrilldown}
                    />

                    <EditableDataRow
                      cellValueSize={5}
                      placeholder="—"
                      name="loanProcessor"
                      label="Processor"
                      type="selectUserEmail"
                      onUpdateFn={handleUpdateField}
                      data={loanDrilldown}
                    />

                    <EditableDataRow
                      cellValueSize={5}
                      placeholder="—"
                      name="loanUnderwriter"
                      label="Underwriter"
                      type="selectUserEmail"
                      onUpdateFn={handleUpdateField}
                      data={loanDrilldown}
                    />

                    <EditableDataRow
                      cellValueSize={5}
                      placeholder="—"
                      name="loanCloser"
                      label="Closer"
                      type="selectUserEmail"
                      onUpdateFn={handleUpdateField}
                      data={loanDrilldown}
                    />
                  </RffLoanDataGroup>
                </TitledGroup>
              </FlxGrid>
              <Flx end fw>
                <Button
                  variant="text"
                  onClick={() => navigate(`${baseUrl}/loan-information`)}
                  endIcon={<EastRounded className="thin" />}
                >
                  Go to Loan Information
                </Button>
              </Flx>
            </Flx>
          </ScreenContent>
        </Suspense>
      </TitledCard>
    </>
  );
};

const LoanProgressStepper = () => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const currentLoanStatus = useMemo(
    () => loanDrilldown?.loanStatus,
    [loanDrilldown?.loanStatus]
  );

  const status_sequentialProgress = [
    "Prospect",
    "Terms Accepted",
    "Processing",
    "Appraisal Ordered",
    "Appraisal In",
    "Underwriting",
    "Final Review",
    "Clear To Close",
  ];

  const finalStatusOptions = [
    "Closed",
    "Closed w/ Post Closing Conditions",
    "Loan Sold",
    "Inactive",
    "Dead",
    "Balance Sheet",
  ];

  const successStatusOptions = [
    "Closed",
    "Closed w/ Post Closing Conditions",
    "Loan Sold",
  ];

  const statusIsFinal = finalStatusOptions.includes(currentLoanStatus);
  const statusIsFinalAndSuccess =
    successStatusOptions.includes(currentLoanStatus);

  let renderedStatusList = [...status_sequentialProgress];

  if (statusIsFinal) {
    renderedStatusList.push(currentLoanStatus);
  }

  let variant = null;

  if (statusIsFinalAndSuccess) {
    variant = "success";
  } else if (statusIsFinal) {
    variant = "error";
  }

  const activeStep = renderedStatusList.indexOf(currentLoanStatus);

  return (
    <Suspense fallback={<LoadingFallback linear />}>
      {/* <ProgressStepper activeStep={activeStep} statusList={statusList} /> */}
      <ProgressStepper
        activeStep={activeStep}
        variant={variant}
        statusList={renderedStatusList}
      />
    </Suspense>
  );
};

const TermsRenderer = ({ cellValueSize, onUpdateFn }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);

  const pt = useMemo(
    () => loanDrilldown?.loanProductType,
    [loanDrilldown?.loanProductType]
  );

  const showHoldback = useMemo(() => {
    if (isNil(pt)) return false;

    if (
      pt === "Fix and Flip" ||
      pt === "Ground Up Construction" ||
      pt === "Vacant Land"
    ) {
      return true;
    }

    return false;
  }, [pt]);

  if (pt === "DSCR") {
    return (
      <>
        <EditableDataRow
          cellValueSize={cellValueSize}
          name="outstandingLoanUpb"
          label="Outstanding Loan UPB"
          type="dollar"
          onUpdateFn={onUpdateFn}
          data={loanDrilldown}
        />
        <EditableDataRow
          cellValueSize={cellValueSize}
          placeholder="—"
          name="interestRate"
          label="Interest Rate"
          type="percent"
          onUpdateFn={onUpdateFn}
          data={loanDrilldown}
        />
        <EditableDataRow
          cellValueSize={cellValueSize}
          placeholder="—"
          name="interestCalcType"
          label="Interest Calc Type"
          type="select"
          options={selectOptionsInterestCalcType}
          onUpdateFn={onUpdateFn}
          data={loanDrilldown}
        />
      </>
    );
  }

  return (
    <>
      {showHoldback && (
        <EditableDataRow
          cellValueSize={cellValueSize}
          placeholder="—"
          name="totalHoldback"
          label="Total Holdback"
          type="dollar"
          onUpdateFn={onUpdateFn}
          data={loanDrilldown}
        />
      )}
      <EditableDataRow
        cellValueSize={cellValueSize}
        placeholder="—"
        name="interestRate"
        label="Interest Rate"
        type="percent"
        onUpdateFn={onUpdateFn}
        data={loanDrilldown}
      />
      <EditableDataRow
        cellValueSize={cellValueSize}
        placeholder="—"
        name="interestCalcType"
        label="Interest Calc Type"
        type="select"
        options={selectOptionsInterestCalcType}
        onUpdateFn={onUpdateFn}
        data={loanDrilldown}
      />
    </>
  );
};

const LoanStatusField = () => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const dispatch = useDispatch();
  const { loading, updateMilestoneStatus } = useUnderwritingHook();
  const [status, setStatus] = useState(null);

  const handleUpdateLoanStatus = useCallback(
    (newStatus) => {
      const oldStatus = loanDrilldown?.loanStatus;

      if (isEqual(newStatus, oldStatus)) return;
      setStatus(newStatus);

      updateMilestoneStatus({
        loanId: loanDrilldown?._id,
        loanStatus: newStatus,
        onSuccessFn: (res) => {
          dispatch(loanDrilldownSet(res));
          dispatch(loanPipelineSet(null));
        },
        onFailFn: () => {
          setStatus(oldStatus);
          dispatch(loanDrilldownSet(loanDrilldown));
        },
      });
    },
    [loanDrilldown, dispatch, updateMilestoneStatus]
  );

  useEffect(() => {
    setStatus(loanDrilldown?.loanStatus);
  }, [loanDrilldown]);

  return (
    <Flx
      ac
      gap={4}
      sx={{
        ".MuiSelect-select": {
          fontSize: "12px",
          padding: "6px 8px",
        },
      }}
    >
      {/* <LoanPipelineInput /> */}
      <Flx ac g={0.65}>
        <Txt secondary>Status</Txt>
        <Box
          sx={{
            position: "relative",
            width: status === "Closed w/ Post Closing Conditions" ? 220 : 140,
          }}
        >
          <Suspense fallback={<LoadingFallback />}>
            <SelectInput
              value={status}
              onChange={handleUpdateLoanStatus}
              options={selectOptionsLoanStatus}
            />
          </Suspense>
          {loading && (
            <LinearProgress
              sx={{
                position: "absolute",
                left: "0px",
                bottom: "0px",
                borderRadius: "2px",
                width: "100%",
              }}
            />
          )}
        </Box>
      </Flx>
    </Flx>
  );
};

export default SummaryTabSummaryCard;
